import { IonIcon } from '@ionic/react';
import { useRfidProfileAvatarQuery } from '../../query/rfidProfileAvatarQuery';
import type { RfidProfile } from '../../models/RfidProfile';
import { getRfidDisplayLabel } from '../../utils/rfidDisplay';
import { useComponentTranslation } from '../../hooks/useTranslation';
import styles from './stats.module.css';

/**
 * "When each cat …" card (Stats Modal Treatments v2): one row per cat with
 * avatar, transit count, a share/time-window sub line supplied by the
 * caller, and a 24-hour mini histogram. Unidentified transits are a footer
 * line rather than a row, so chip-read gaps stay visible without competing
 * with the cats.
 */

export interface CatHoursRow {
    rfidCode: string;
    /** Merged household profile for the chip, when one exists. */
    profile?: RfidProfile;
    transits: number;
    /** Pre-localized share/time-window line, or null to omit. */
    sub: string | null;
    /** 24 transit counts by device-local hour. */
    hourHistogram: number[];
}

const HOUR_LABELS = ['00', '06', '12', '18', '23'];

const HourSparkline: React.FC<{ values: number[]; showLabels: boolean }> = ({ values, showLabels }) => {
    const max = Math.max(...values, 1);
    return (
        <div>
            <div className={styles.sparkline}>
                {values.map((value, hour) => (
                    <div
                        key={hour}
                        className={`${styles.sparklineBar} ${value === max ? styles.sparklineBarPeak : ''}`}
                        style={{ height: Math.max(3, Math.round((value / max) * 40)) }}
                    />
                ))}
            </div>
            {showLabels && (
                <div className={styles.sparklineLabels}>
                    {HOUR_LABELS.map((label) => <span key={label}>{label}</span>)}
                </div>
            )}
        </div>
    );
};

const RowAvatar: React.FC<{ rfidCode: string; profile?: RfidProfile }> = ({ rfidCode, profile }) => {
    const avatarQuery = useRfidProfileAvatarQuery(profile?.avatarUserId ?? undefined, rfidCode, profile?.avatarUpdatedAt);
    return (
        <span className={styles.catAvatar}>
            {avatarQuery.data
                ? <img src={avatarQuery.data} alt="" className={styles.catAvatarImage} />
                : <IonIcon src="/assets/activity/activity-icon.svg" aria-hidden />}
        </span>
    );
};

const CatHoursCard: React.FC<{ rows: CatHoursRow[]; unidentifiedTransits: number }> = ({ rows, unidentifiedTransits }) => {
    const { t } = useComponentTranslation();
    return (
        <div className={`${styles.card} ${styles.catHoursCard}`}>
            {rows.map((row, index) => (
                <div key={row.rfidCode} className={styles.catHoursRow}>
                    <div className={styles.catHoursHead}>
                        <RowAvatar rfidCode={row.rfidCode} profile={row.profile} />
                        <div className={styles.catHoursBody}>
                            <div className={styles.catHoursTitle}>
                                <span className={styles.catHoursName}>{getRfidDisplayLabel(row.rfidCode, row.profile?.label)}</span>
                                <span className={styles.catHoursValue}>{row.transits}</span>
                                <span className={styles.catHoursCaption}>{t('stats.transits', { count: row.transits })}</span>
                            </div>
                            {row.sub && <div className={styles.catHoursSub}>{row.sub}</div>}
                        </div>
                    </div>
                    <HourSparkline values={row.hourHistogram} showLabels={index === rows.length - 1} />
                </div>
            ))}
            <div className={styles.catHoursFooter}>
                <span className={styles.catHoursFooterLabel}>{t('stats.unidentified_note')}</span>
                <span className={styles.catHoursFooterValue}>{unidentifiedTransits}</span>
            </div>
        </div>
    );
};

export default CatHoursCard;
